/**
 * Admin — Location Maps designer.
 *
 * Maps are a named registry: each one names a dataset (retailers / trusted installers on
 * trusscore.com), a data filter, how it looks, and its Handlebars templates. Action buttons
 * reference a map by id ("Open a Location Map"); the Inline placement panel below decides
 * which map renders under the BOM tables — universally, or per retailer.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MapPinned, Plus, Trash2, Loader2, Save, Copy, AlertTriangle, RotateCcw, ChevronLeft,
} from 'lucide-react';
import {
  fetchLocationMapsFull, fetchMapFacets, createLocationMap, updateLocationMap, deleteLocationMap, savePlacement,
  MapReferencedError, type LocationMapsListResponse, type RetailerFacets, type InstallerFacets, type LocationMapPayload,
  type LocationMapPlacementKey,
} from '../../services/adminLocationMapsApi';
import {
  type AdminLocationMap, type LocationMapFilter, type LocationMapDisplay, type MapDataset, type MapLockPreset,
  type MapListPosition, type MapCenterStrategy, type MapThemeTokens, type ThemeTokenName,
  DEFAULT_DISPLAY, DEFAULT_FILTER, DEFAULT_TEMPLATES, SAMPLE_LOCATION, PLACEHOLDER_LEGEND, HELPER_LEGEND,
  MAP_STRING_KEYS, LOCK_PRESETS, THEME_TOKEN_SCHEMA,
} from '../../config/locationMaps';
import { renderTemplateOnce, type LocationView } from '../../components/maps/mapTemplates';
import { ColorField } from '../../components/admin/ColorField';
import { IconPicker } from '../../components/admin/IconPicker';
import { DynamicActionIcon } from '../../components/results/DynamicActionIcon';
import { TranslationFields } from '../../components/admin/TranslationFields';
import { saveTranslations, type TranslationValues } from '../../services/translationsApi';
import { fetchGeoProviders, type GeoProviderMeta } from '../../services/api';
import '../../components/maps/LocationMap.css';

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

function newDraft(): AdminLocationMap {
  return {
    id: 0, name: 'New map', dataset: 'retailers',
    filter: clone(DEFAULT_FILTER), display: clone(DEFAULT_DISPLAY), templates: { ...DEFAULT_TEMPLATES },
    strings: {}, isActive: true, updatedAt: '',
  };
}

/** Tokens offered as colour pickers / numbers / switches (the rest come in via "Paste theme JSON"). */
const THEME_COLOR_TOKENS: { token: ThemeTokenName; label: string }[] = [
  { token: 'water', label: 'Water' }, { token: 'land', label: 'Land' }, { token: 'greenspace', label: 'Parks / green' },
  { token: 'buildings', label: 'Buildings' }, { token: 'roadMajor', label: 'Major roads' }, { token: 'roadMinor', label: 'Minor roads' },
  { token: 'motorway', label: 'Motorways' }, { token: 'boundary', label: 'Country borders' }, { token: 'boundaryState', label: 'State borders' },
  { token: 'labelText', label: 'Label text' }, { token: 'labelHalo', label: 'Label halo' },
  { token: 'maskColor', label: 'Lock mask' }, { token: 'lockOutline', label: 'Lock outline' },
  { token: 'accent', label: 'Accent (focus, fallback pin)' }, { token: 'pinHighlight', label: 'Highlighted pin' },
  { token: 'listBg', label: 'List background' }, { token: 'cardBg', label: 'Card background' }, { token: 'cardBorder', label: 'Card border' },
  { token: 'popupBg', label: 'Popup background' }, { token: 'popupFg', label: 'Popup text' },
];
const THEME_BOOL_TOKENS: { token: ThemeTokenName; label: string }[] = [
  { token: 'poiVisible', label: 'Points of interest' }, { token: 'buildingsVisible', label: 'Buildings' },
  { token: 'shieldsVisible', label: 'Road shields' }, { token: 'housenumbersVisible', label: 'House numbers' },
];

const csv = (v: string[] | undefined) => (v ?? []).join(', ');
const parseCsv = (s: string) => s.split(/[\n,]+/).map(x => x.trim()).filter(Boolean);

export const AdminLocationMaps: React.FC = () => {
  const [data, setData] = useState<LocationMapsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | 'new' | null>(null);
  const [draft, setDraft] = useState<AdminLocationMap | null>(null);
  const [translations, setTranslations] = useState<TranslationValues>({});
  const [facets, setFacets] = useState<RetailerFacets | InstallerFacets | null>(null);
  const [facetsNote, setFacetsNote] = useState<string | null>(null);
  const [providers, setProviders] = useState<GeoProviderMeta[]>([]);
  const [themeJson, setThemeJson] = useState('');
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  // Inline placement panel
  const [scope, setScope] = useState<number | null>(null);
  const [placementChoice, setPlacementChoice] = useState<string>('');
  // Which slot: inline under the BOM, or one of the share picker's two tabs.
  const [placementKind, setPlacementKind] = useState<LocationMapPlacementKey>('below_bom');
  const [placementSaving, setPlacementSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchLocationMapsFull();
      setData(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { fetchGeoProviders().then(r => { if (r) setProviders(r.providers); }).catch(() => { /* optional */ }); }, []);

  // Facets follow the draft's dataset.
  const dataset = draft?.dataset;
  useEffect(() => {
    if (!dataset) return;
    let cancelled = false;
    setFacets(null);
    setFacetsNote(null);
    fetchMapFacets(dataset).then(r => {
      if (cancelled) return;
      if ('notConfigured' in r) { setFacetsNote('The trusscore.com endpoint is not configured yet (Settings → Integrations → Location Maps) — type values by hand for now.'); return; }
      setFacets(r.facets);
      if (r.stale) setFacetsNote('Showing cached values — trusscore.com did not answer just now.');
    }).catch(e => { if (!cancelled) setFacetsNote(`Could not load values from trusscore.com: ${e instanceof Error ? e.message : 'error'}`); });
    return () => { cancelled = true; };
  }, [dataset]);

  // Placement panel state follows the scope + data.
  useEffect(() => {
    if (!data) return;
    const row = data.placements.find(p => p.retailerId === scope && p.placement === placementKind);
    if (!row) setPlacementChoice(scope === null ? '' : 'inherit');
    else setPlacementChoice(row.mapId === null ? '' : String(row.mapId));
  }, [data, scope, placementKind]);

  const select = (id: number | 'new') => {
    setSelected(id);
    setMessage(null);
    setError(null);
    setTranslations({});
    setThemeJson('');
    if (id === 'new') setDraft(newDraft());
    else {
      const m = data?.maps.find(x => x.id === id);
      setDraft(m ? clone(m) : null);
    }
  };
  const duplicate = (m: AdminLocationMap) => {
    setSelected('new');
    setDraft({ ...clone(m), id: 0, name: `${m.name} (copy)`, updatedAt: '' });
    setTranslations({});
    setMessage(null);
  };

  const patchDraft = (p: Partial<AdminLocationMap>) => setDraft(d => (d ? { ...d, ...p } : d));
  const patchFilter = (p: Partial<LocationMapFilter>) => setDraft(d => (d ? { ...d, filter: { ...d.filter, ...p } } : d));
  const patchDisplay = (p: Partial<LocationMapDisplay>) => setDraft(d => (d ? { ...d, display: { ...d.display, ...p } } : d));
  const patchTheme = (token: ThemeTokenName, value: string | number | boolean | undefined) => setDraft(d => {
    if (!d) return d;
    const theme: MapThemeTokens = { ...d.display.theme };
    if (value === undefined || value === '') delete theme[token];
    else theme[token] = value;
    return { ...d, display: { ...d.display, theme } };
  });

  const applyThemeJson = () => {
    try {
      const parsed = JSON.parse(themeJson);
      const tokens = (parsed && typeof parsed === 'object' && parsed.tokens && typeof parsed.tokens === 'object') ? parsed.tokens : parsed;
      const theme: MapThemeTokens = { ...(draft?.display.theme ?? {}) };
      let n = 0;
      for (const [k, v] of Object.entries(tokens as Record<string, unknown>)) {
        if (!(k in THEME_TOKEN_SCHEMA)) continue;
        const kind = THEME_TOKEN_SCHEMA[k as ThemeTokenName];
        if (kind === 'color' && typeof v === 'string') { theme[k as ThemeTokenName] = v; n++; }
        else if (kind === 'number' && typeof v === 'number') { theme[k as ThemeTokenName] = v; n++; }
        else if (kind === 'bool' && typeof v === 'boolean') { theme[k as ThemeTokenName] = v; n++; }
      }
      patchDisplay({ theme });
      setMessage(`${n} theme token${n === 1 ? '' : 's'} applied.`);
      setThemeJson('');
    } catch {
      setError('That is not valid JSON.');
    }
  };

  // Template preview through the same pipeline the results page uses.
  const sampleView: LocationView = useMemo(() => ({
    ...SAMPLE_LOCATION, hasDistance: true, distanceText: '12.4 km away', showUnits: 'km',
    // Share-picker fields: previewed as an idle, selectable card.
    selectable: true, isSelected: false, isDisabled: false, selectLabel: 'Send estimate here',
  }), []);
  const preview = useMemo(() => {
    if (!draft) return null;
    const out: { card?: string; info?: string; none?: string; error?: string } = {};
    try { out.card = renderTemplateOnce(draft.templates.card, sampleView, draft.strings); } catch (e) { out.error = `Card: ${(e as Error).message}`; }
    try { out.info = renderTemplateOnce(draft.templates.infowindow, sampleView, draft.strings); } catch (e) { out.error = out.error ?? `Popup: ${(e as Error).message}`; }
    try { out.none = renderTemplateOnce(draft.templates.noresults, { hasOrigin: false }, draft.strings); } catch (e) { out.error = out.error ?? `No results: ${(e as Error).message}`; }
    return out;
  }, [draft, sampleView]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload: LocationMapPayload = {
        name: draft.name, dataset: draft.dataset, filter: draft.filter, display: draft.display,
        templates: draft.templates, strings: draft.strings, isActive: draft.isActive,
      };
      const res = draft.id > 0 ? await updateLocationMap(draft.id, payload) : await createLocationMap(payload);
      try {
        if (Object.keys(translations).length) await saveTranslations('location_maps', res.map.id, translations);
        setMessage('Saved. Reload an estimate to see the change.');
      } catch {
        setMessage('Map saved, but the translations could not be stored. Try saving again.');
      }
      await load();
      setSelected(res.map.id);
      setDraft(clone(res.map));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m: AdminLocationMap) => {
    if (!window.confirm(`Delete the map "${m.name}"?`)) return;
    setSaving(true);
    setError(null);
    try {
      await deleteLocationMap(m.id);
      if (selected === m.id) { setSelected(null); setDraft(null); }
      await load();
      setMessage('Map deleted.');
    } catch (e) {
      if (e instanceof MapReferencedError) {
        const refs = e.references;
        const parts: string[] = [];
        if (refs.actionBars.length) parts.push(`action button${refs.actionBars.length === 1 ? '' : 's'} on ${refs.actionBars.map(r => (r.retailerId === null ? 'the Universal bar' : `retailer #${r.retailerId}`)).join(', ')}`);
        if (refs.placements.length) parts.push(`inline placement for ${refs.placements.map(r => (r.retailerId === null ? 'Universal' : `retailer #${r.retailerId}`)).join(', ')}`);
        setError(`Still in use by ${parts.join(' and ')}. Remove those references first.`);
      } else {
        setError(e instanceof Error ? e.message : 'Delete failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const savePlacementChoice = async () => {
    setPlacementSaving(true);
    setError(null);
    try {
      const mapId = placementChoice === 'inherit' ? 'inherit' : placementChoice === '' ? null : Number(placementChoice);
      await savePlacement(scope, mapId, placementKind);
      await load();
      setMessage('Placement saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the placement');
    } finally {
      setPlacementSaving(false);
    }
  };

  const usedBy = (id: number) => {
    const placements = data?.placements.filter(p => p.mapId === id).length ?? 0;
    return placements;
  };

  const retailerFacets = facets && 'buyingGroups' in facets ? facets : null;
  const installerFacets = facets && 'industries' in facets ? facets : null;
  const countryOptions = facets ? facets.countries : [];
  const regionOptions = useMemo(() => {
    if (!facets) return [] as { value: string; label: string; country: string }[];
    const selectedCountries = draft?.filter.countries ?? [];
    const rows = retailerFacets ? retailerFacets.regions.map(r => ({ value: r.code ?? r.value, label: `${r.name || r.value}${r.code ? ` (${r.code})` : ''} · ${r.count}`, country: r.country }))
      : installerFacets ? installerFacets.regions.map(r => ({ value: r.code, label: `${r.name || r.code} (${r.code}) · ${r.count}`, country: r.country })) : [];
    return selectedCountries.length ? rows.filter(r => selectedCountries.includes(r.country)) : rows;
  }, [facets, retailerFacets, installerFacets, draft?.filter.countries]);

  const toggleInList = (list: string[] | undefined, value: string): string[] | undefined => {
    const cur = list ?? [];
    const next = cur.includes(value) ? cur.filter(x => x !== value) : [...cur, value];
    return next.length ? next : undefined;
  };

  const heading = draft?.display.heading ?? DEFAULT_DISPLAY.heading!;
  const patchHeading = (p: Partial<NonNullable<LocationMapDisplay['heading']>>) => patchDisplay({ heading: { ...heading, ...p } });

  const translatableFields = draft ? [
    { path: 'display_json.heading.title', label: 'Inline heading title', english: heading.title },
    { path: 'display_json.heading.subtitle', label: 'Inline heading subtitle', english: heading.subtitle },
    ...MAP_STRING_KEYS.map(k => ({ path: `strings_json.${k}`, label: `String: ${k}`, english: draft.strings[k] ?? '' })),
    { path: 'display_json.search.placeholder', label: 'Search placeholder', english: draft.display.search.placeholder ?? '' },
  ] : [];

  return (
    <div className="bg-white rounded-3 shadow-sm p-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h2 className="fw-bold mb-1 d-flex align-items-center gap-2"><MapPinned size={22} /> Location Maps</h2>
          <p className="text-muted mb-0">
            Retailer and trusted-installer maps for estimate results. Design a map here, then open it from an
            action button (<a href="/admin/action-buttons">Action Buttons</a> → &quot;Open a Location Map&quot;)
            and/or show it inline below the materials list with the placement panel at the bottom.
          </p>
        </div>
        {selected !== null && (
          <button type="button" className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1" onClick={() => { setSelected(null); setDraft(null); }}>
            <ChevronLeft size={14} /> All maps
          </button>
        )}
      </div>

      {data && !data.upstreamConfigured && (
        <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2">
          <AlertTriangle size={14} /> The trusscore.com location endpoint is not configured — maps will render empty until the URL and key are set under Settings → Integrations → Location Maps.
        </div>
      )}
      {error && <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2"><AlertTriangle size={14} /> {error}</div>}
      {message && <div className="alert alert-success py-2 px-3 small">{message}</div>}

      {loading && <div className="text-muted small d-flex align-items-center gap-2 py-4"><Loader2 size={16} className="animate-spin" /> Loading…</div>}

      {/* ---------------- List ---------------- */}
      {!loading && data && selected === null && (
        <>
          <div className="d-flex justify-content-end mb-2">
            <button type="button" className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" onClick={() => select('new')}><Plus size={14} /> New map</button>
          </div>
          {data.maps.length === 0 ? (
            <div className="text-muted small py-4 text-center border rounded-3">No maps yet. Create one to get started.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead><tr><th>Name</th><th>Dataset</th><th>Filter</th><th>Active</th><th>Inline placements</th><th /></tr></thead>
                <tbody>
                  {data.maps.map(m => (
                    <tr key={m.id}>
                      <td><button type="button" className="btn btn-link p-0 fw-semibold text-decoration-none" onClick={() => select(m.id)}>{m.name}</button></td>
                      <td>{m.dataset}</td>
                      <td className="small text-muted">
                        {[
                          m.filter.ids?.length ? `${m.filter.ids.length} id${m.filter.ids.length === 1 ? '' : 's'}` : null,
                          m.filter.buyingGroups?.length ? m.filter.buyingGroups.join(', ') : null,
                          m.filter.regions?.length ? m.filter.regions.join(', ') : null,
                          m.filter.countries?.length ? m.filter.countries.join(', ') : null,
                          m.filter.radiusKm ? `${m.filter.radiusKm} km around ${m.filter.radiusAround === 'visitor' ? 'the visitor' : 'the estimate'}` : null,
                          m.filter.useRetailerFilter ? 'portal filter' : null,
                        ].filter(Boolean).join(' · ') || 'everything'}
                      </td>
                      <td>{m.isActive ? <span className="badge text-bg-success">Active</span> : <span className="badge text-bg-secondary">Inactive</span>}</td>
                      <td>{usedBy(m.id) || '—'}</td>
                      <td className="text-end text-nowrap">
                        <button type="button" className="btn btn-link btn-sm p-0 me-3" onClick={() => duplicate(m)}><Copy size={13} /> Duplicate</button>
                        <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => void remove(m)} disabled={saving}><Trash2 size={13} /> Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ---------------- Inline placement ---------------- */}
          <div className="border rounded-3 p-3 mt-4">
            <h5 className="fw-semibold mb-1">Placements</h5>
            <p className="text-muted small mb-3">
              Where maps appear: inline below the materials list, and as the Retailers / Installers tabs of the estimate <strong>share picker</strong>.
              The Universal choice applies to every retailer; a retailer row replaces it (&quot;None&quot; hides that slot for the retailer — a share-picker tab with no map is hidden, and with both hidden the share button disappears).
              Keep the list visible on share-picker maps: it is the keyboard path to selecting a location. Maps opened from action buttons are configured on the Action Buttons page instead.
            </p>
            <div className="d-flex flex-wrap align-items-end gap-2">
              <div>
                <label className="form-label small fw-semibold mb-1">Slot</label>
                <select className="form-select" style={{ minWidth: 240 }} value={placementKind} onChange={e => setPlacementKind(e.target.value as LocationMapPlacementKey)}>
                  <option value="below_bom">Inline — below the materials list</option>
                  <option value="share_retailers">Share picker — Retailers tab</option>
                  <option value="share_installers">Share picker — Installers tab</option>
                </select>
              </div>
              <div>
                <label className="form-label small fw-semibold mb-1">Retailer</label>
                <select className="form-select" style={{ minWidth: 240 }} value={scope === null ? 'universal' : String(scope)} onChange={e => setScope(e.target.value === 'universal' ? null : Number(e.target.value))}>
                  <option value="universal">Universal (every retailer)</option>
                  {data.retailers.filter(r => r.id !== 1).map(r => {
                    const custom = data.placements.some(p => p.retailerId === r.id && p.placement === placementKind);
                    return <option key={r.id} value={r.id}>{r.name}{custom ? ' — custom' : ''}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="form-label small fw-semibold mb-1">Map</label>
                <select className="form-select" style={{ minWidth: 240 }} value={placementChoice} onChange={e => setPlacementChoice(e.target.value)}>
                  {scope !== null && <option value="inherit">Inherit the Universal choice</option>}
                  <option value="">None</option>
                  {data.maps
                    .filter(m => m.isActive)
                    .filter(m => placementKind === 'below_bom' || (placementKind === 'share_retailers' ? m.dataset === 'retailers' : m.dataset === 'installers'))
                    .map(m => <option key={m.id} value={m.id}>{m.name} ({m.dataset})</option>)}
                </select>
              </div>
              <button type="button" className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" onClick={() => void savePlacementChoice()} disabled={placementSaving}>
                {placementSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save placement
              </button>
            </div>
          </div>
        </>
      )}

      {/* ---------------- Editor ---------------- */}
      {!loading && draft && selected !== null && (
        <>
          {/* Basics */}
          <div className="border rounded-3 p-3 mb-3">
            <h5 className="fw-semibold mb-2">Basics</h5>
            <div className="row g-2 align-items-end">
              <div className="col-sm-5">
                <label className="form-label small fw-semibold mb-1">Name (admin only)</label>
                <input className="form-control" value={draft.name} maxLength={120} onChange={e => patchDraft({ name: e.target.value })} />
              </div>
              <div className="col-sm-4">
                <label className="form-label small fw-semibold mb-1">Dataset</label>
                <select className="form-select" value={draft.dataset} onChange={e => { const ds = e.target.value as MapDataset; patchDraft({ dataset: ds, filter: { ...draft.filter, buyingGroups: undefined, types: undefined, stocking: undefined, installerFlags: undefined, serves: undefined, regions: undefined } }); }}>
                  <option value="retailers">Retailers (trusscore.com retailer database)</option>
                  <option value="installers">Trusted Installers</option>
                </select>
              </div>
              <div className="col-sm-3">
                <div className="form-check form-switch small">
                  <input type="checkbox" className="form-check-input" role="switch" id="lm-active" checked={draft.isActive} onChange={e => patchDraft({ isActive: e.target.checked })} />
                  <label className="form-check-label" htmlFor="lm-active">Active</label>
                </div>
              </div>
            </div>
          </div>

          {/* Data filter */}
          <div className="border rounded-3 p-3 mb-3">
            <h5 className="fw-semibold mb-1">Which locations</h5>
            <p className="text-muted small mb-2">Values come live from trusscore.com. Leave everything blank to show the whole dataset (viewport-loaded, capped by the limit).</p>
            {facetsNote && <div className="small text-warning mb-2">{facetsNote}</div>}
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label small fw-semibold mb-1">Countries</label>
                {countryOptions.length ? (
                  <div className="border rounded p-2" style={{ maxHeight: 160, overflowY: 'auto' }}>
                    {countryOptions.map(c => (
                      <div className="form-check small" key={c.code}>
                        <input type="checkbox" className="form-check-input" id={`lm-c-${c.code}`} checked={(draft.filter.countries ?? []).includes(c.code)} onChange={() => patchFilter({ countries: toggleInList(draft.filter.countries, c.code) })} />
                        <label className="form-check-label" htmlFor={`lm-c-${c.code}`}>{c.name || c.code} ({c.code}) · {c.count}</label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <input className="form-control" placeholder="CA, US" value={csv(draft.filter.countries)} onChange={e => patchFilter({ countries: parseCsv(e.target.value).map(s => s.toUpperCase()) })} />
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold mb-1">Regions</label>
                {regionOptions.length ? (
                  <div className="border rounded p-2" style={{ maxHeight: 160, overflowY: 'auto' }}>
                    {regionOptions.map(r => (
                      <div className="form-check small" key={`${r.country}-${r.value}`}>
                        <input type="checkbox" className="form-check-input" id={`lm-r-${r.country}-${r.value}`} checked={(draft.filter.regions ?? []).includes(r.value)} onChange={() => patchFilter({ regions: toggleInList(draft.filter.regions, r.value) })} />
                        <label className="form-check-label" htmlFor={`lm-r-${r.country}-${r.value}`}>{r.label}</label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <input className="form-control" placeholder="ON, QC" value={csv(draft.filter.regions)} onChange={e => patchFilter({ regions: parseCsv(e.target.value) })} />
                )}
                <input className="form-control mt-2" placeholder="City (optional, needs one region)" value={draft.filter.city ?? ''} onChange={e => patchFilter({ city: e.target.value || undefined })} />
              </div>
              <div className="col-md-4">
                {draft.dataset === 'retailers' ? (
                  <>
                    <label className="form-label small fw-semibold mb-1">Buying groups</label>
                    {retailerFacets && retailerFacets.buyingGroups.length ? (
                      <div className="border rounded p-2" style={{ maxHeight: 160, overflowY: 'auto' }}>
                        {retailerFacets.buyingGroups.map(g => (
                          <div className="form-check small" key={g.value}>
                            <input type="checkbox" className="form-check-input" id={`lm-bg-${g.value}`} checked={(draft.filter.buyingGroups ?? []).includes(g.value)} onChange={() => patchFilter({ buyingGroups: toggleInList(draft.filter.buyingGroups, g.value) })} />
                            <label className="form-check-label" htmlFor={`lm-bg-${g.value}`}>{g.value} · {g.count}</label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <input className="form-control" placeholder="Home Hardware, Castle" value={csv(draft.filter.buyingGroups)} onChange={e => patchFilter({ buyingGroups: parseCsv(e.target.value) })} />
                    )}
                    <div className="d-flex flex-wrap gap-3 mt-2 small">
                      {[1, 2, 3].map(t => (
                        <div className="form-check" key={t}>
                          <input type="checkbox" className="form-check-input" id={`lm-t-${t}`} checked={(draft.filter.types ?? []).includes(t)} onChange={() => { const cur = draft.filter.types ?? []; const next = cur.includes(t) ? cur.filter(x => x !== t) : [...cur, t]; patchFilter({ types: next.length ? next : undefined }); }} />
                          <label className="form-check-label" htmlFor={`lm-t-${t}`}>{t === 1 ? 'Retailers' : t === 2 ? 'Generic' : 'Showrooms'}</label>
                        </div>
                      ))}
                      <div className="form-check form-switch">
                        <input type="checkbox" className="form-check-input" role="switch" id="lm-stocking" checked={!!draft.filter.stocking} onChange={e => patchFilter({ stocking: e.target.checked || undefined })} />
                        <label className="form-check-label" htmlFor="lm-stocking">Stocking only</label>
                      </div>
                    </div>
                    <div className="form-text">Only the first checked type is sent (the source endpoint filters one type per query).</div>
                  </>
                ) : (
                  <>
                    <label className="form-label small fw-semibold mb-1">Installer work</label>
                    <div className="d-flex flex-wrap gap-3 small">
                      {(['residential', 'commercial', 'agriculture'] as const).map(f => (
                        <div className="form-check" key={f}>
                          <input type="checkbox" className="form-check-input" id={`lm-if-${f}`} checked={!!draft.filter.installerFlags?.[f]} onChange={e => { const flags = { ...(draft.filter.installerFlags ?? {}) }; if (e.target.checked) flags[f] = true; else delete flags[f]; patchFilter({ installerFlags: Object.keys(flags).length ? flags : undefined }); }} />
                          <label className="form-check-label" htmlFor={`lm-if-${f}`}>{f[0].toUpperCase() + f.slice(1)}{installerFacets ? ` · ${installerFacets.industries[f]}` : ''}</label>
                        </div>
                      ))}
                    </div>
                    <div className="form-check form-switch small mt-2">
                      <input type="checkbox" className="form-check-input" role="switch" id="lm-serves" checked={!!draft.filter.serves} onChange={e => patchFilter({ serves: e.target.checked || undefined })} />
                      <label className="form-check-label" htmlFor="lm-serves">Only installers whose service radius reaches the search point (radius maps)</label>
                    </div>
                  </>
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold mb-1">Explicit ids ({draft.dataset === 'retailers' ? 'Salesforce account ids' : 'installer ids'})</label>
                <textarea className="form-control" rows={3} placeholder="One per line — overrides every other filter" value={(draft.filter.ids ?? []).join('\n')} onChange={e => patchFilter({ ids: parseCsv(e.target.value).length ? parseCsv(e.target.value) : undefined })} />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold mb-1">Radius</label>
                <div className="input-group">
                  <input type="number" min={1} max={3000} className="form-control" placeholder="km" value={draft.filter.radiusKm ?? ''} onChange={e => patchFilter({ radiusKm: e.target.value === '' ? null : Number(e.target.value) })} />
                  <span className="input-group-text">km around</span>
                  <select className="form-select" value={draft.filter.radiusAround ?? 'estimateAddress'} onChange={e => patchFilter({ radiusAround: e.target.value as 'estimateAddress' | 'visitor' })}>
                    <option value="estimateAddress">the estimate&apos;s address</option>
                    <option value="visitor">the visitor / search point</option>
                  </select>
                </div>
                <div className="form-text">Blank = no radius. &quot;Estimate&apos;s address&quot; uses the saved city / postal code (never the street); &quot;visitor&quot; waits for a search or geolocation.</div>
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold mb-1">Limit</label>
                <input type="number" min={1} max={500} className="form-control" value={draft.filter.limit ?? 300} onChange={e => patchFilter({ limit: Number(e.target.value) })} />
                <div className="form-check form-switch small mt-2">
                  <input type="checkbox" className="form-check-input" role="switch" id="lm-urf" checked={!!draft.filter.useRetailerFilter} onChange={e => patchFilter({ useRetailerFilter: e.target.checked || undefined })} />
                  <label className="form-check-label" htmlFor="lm-urf">Use the portal&apos;s retailer filter (ids / buying group set on the Retailer)</label>
                </div>
              </div>
            </div>
          </div>

          {/* Inline heading */}
          <div className="border rounded-3 p-3 mb-3">
            <h5 className="fw-semibold mb-1">Heading (inline map only)</h5>
            <p className="text-muted small mb-2">Shown above the map when it renders below the materials list — the same title block as the Placement Guide, so retailer theming applies. Not used in the modal (that has its own title bar).</p>
            <div className="row g-2 align-items-end">
              <div className="col-md-2">
                <div className="form-check form-switch"><input type="checkbox" className="form-check-input" role="switch" id="lm-hd-show" checked={heading.show} onChange={e => patchHeading({ show: e.target.checked })} /><label className="form-check-label" htmlFor="lm-hd-show">Show heading</label></div>
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-semibold mb-1">Icon</label>
                <div className="d-flex align-items-center gap-2">
                  <span className="border rounded-3 d-inline-flex align-items-center justify-content-center" style={{ width: 42, height: 42 }}>
                    {heading.icon ? <DynamicActionIcon icon={heading.icon} size={22} /> : <span className="text-muted small">none</span>}
                  </span>
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setIconPickerOpen(true)}>Choose</button>
                  {heading.icon && <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => patchHeading({ icon: null })}>Remove</button>}
                </div>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold mb-1">Title</label>
                <input className="form-control" maxLength={120} placeholder="Where to buy" value={heading.title} onChange={e => patchHeading({ title: e.target.value })} />
              </div>
              <div className="col-md-5">
                <label className="form-label small fw-semibold mb-1">Subtitle</label>
                <input className="form-control" maxLength={300} placeholder="Find a retailer near you that carries Trusscore." value={heading.subtitle} onChange={e => patchHeading({ subtitle: e.target.value })} />
              </div>
            </div>
            {heading.show && heading.title.trim() !== '' && (
              <div className="lm__heading mt-3 mb-0 border rounded-3 p-3 bg-light">
                <h2 className="section-title">{heading.icon && <span className="section-title-icon"><DynamicActionIcon icon={heading.icon} size={22} /></span>}{heading.title}</h2>
                {heading.subtitle.trim() !== '' && <p className="section-description">{heading.subtitle}</p>}
              </div>
            )}
          </div>

          {/* Display */}
          <div className="border rounded-3 p-3 mb-3">
            <h5 className="fw-semibold mb-2">Display</h5>
            <div className="row g-2">
              <div className="col-md-6">
                <label className="form-label small fw-semibold mb-1">Tile style URL (blank = the default from Settings)</label>
                <input className="form-control" placeholder="https://tiles.openfreemap.org/styles/liberty" value={draft.display.styleUrl ?? ''} onChange={e => patchDisplay({ styleUrl: e.target.value || null })} />
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-semibold mb-1">Inline height (px)</label>
                <input type="number" min={320} max={900} className="form-control" value={draft.display.heightPx} onChange={e => patchDisplay({ heightPx: Number(e.target.value) })} />
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-semibold mb-1">Modal max height (px)</label>
                <input type="number" min={320} max={1400} className="form-control" value={draft.display.modalMaxHeightPx ?? 640} onChange={e => patchDisplay({ modalMaxHeightPx: Number(e.target.value) })} />
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-semibold mb-1">Mobile max height (px)</label>
                <input type="number" min={240} max={900} className="form-control" value={draft.display.mobileMaxHeightPx ?? 360} onChange={e => patchDisplay({ mobileMaxHeightPx: Number(e.target.value) })} />
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-semibold mb-1">Pin size</label>
                <input type="number" min={24} max={64} className="form-control" value={draft.display.pinSize} onChange={e => patchDisplay({ pinSize: Number(e.target.value) })} />
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-semibold mb-1">Units</label>
                <select className="form-select" value={draft.display.units} onChange={e => patchDisplay({ units: e.target.value as LocationMapDisplay['units'] })}>
                  <option value="auto">Default (Settings)</option><option value="km">km</option><option value="miles">miles</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold mb-1">Lock to region</label>
                <select className="form-select" value={draft.display.lockPreset} onChange={e => patchDisplay({ lockPreset: e.target.value as MapLockPreset })}>
                  <option value="none">No lock</option>
                  {(Object.keys(LOCK_PRESETS) as Exclude<MapLockPreset, 'none'>[]).map(k => <option key={k} value={k}>{LOCK_PRESETS[k].label}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold mb-1">List of cards</label>
                <select className="form-select" value={draft.display.listPosition} onChange={e => patchDisplay({ listPosition: e.target.value as MapListPosition })}>
                  <option value="right">Right of the map</option><option value="left">Left of the map</option><option value="below">Below the map</option><option value="hidden">Hidden</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold mb-1">Initial view</label>
                <select className="form-select" value={draft.display.initialCenter} onChange={e => patchDisplay({ initialCenter: e.target.value as MapCenterStrategy })}>
                  <option value="fit">Fit all locations</option><option value="estimateAddress">The estimate&apos;s address</option><option value="visitor">The visitor (IP location)</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-semibold mb-1">Zoom after a search / centre</label>
                <input type="number" min={1} max={18} step={0.5} className="form-control" value={draft.display.defaultZoom ?? 11} onChange={e => patchDisplay({ defaultZoom: Number(e.target.value) })} />
              </div>
              <div className="col-12 d-flex flex-wrap gap-4 small mt-1">
                <div className="form-check form-switch"><input type="checkbox" className="form-check-input" role="switch" id="lm-sd" checked={draft.display.showDistance} onChange={e => patchDisplay({ showDistance: e.target.checked })} /><label className="form-check-label" htmlFor="lm-sd">Show distances</label></div>
                <div className="form-check form-switch"><input type="checkbox" className="form-check-input" role="switch" id="lm-geo" checked={draft.display.geolocate} onChange={e => patchDisplay({ geolocate: e.target.checked })} /><label className="form-check-label" htmlFor="lm-geo">&quot;Use my location&quot; button</label></div>
              </div>
            </div>

            <h6 className="fw-semibold small mt-3 mb-2">Search box</h6>
            <div className="row g-2 align-items-end">
              <div className="col-md-2">
                <div className="form-check form-switch small"><input type="checkbox" className="form-check-input" role="switch" id="lm-se" checked={draft.display.search.enabled} onChange={e => patchDisplay({ search: { ...draft.display.search, enabled: e.target.checked } })} /><label className="form-check-label" htmlFor="lm-se">Enabled</label></div>
              </div>
              <div className="col-md-3">
                <label className="form-label small mb-1">Provider</label>
                <select className="form-select" value={draft.display.search.provider ?? ''} onChange={e => patchDisplay({ search: { ...draft.display.search, provider: e.target.value || null } })}>
                  <option value="">Default (Settings)</option>
                  {providers.map(p => <option key={p.name} value={p.name}>{p.label}{p.configured ? '' : ' — needs key'}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label small mb-1">Placeholder (blank = translated default)</label>
                <input className="form-control" value={draft.display.search.placeholder ?? ''} onChange={e => patchDisplay({ search: { ...draft.display.search, placeholder: e.target.value } })} />
              </div>
              <div className="col-md-3">
                <label className="form-label small mb-1">Restrict to countries</label>
                <input className="form-control" placeholder="blank = the lock / filter countries" value={csv(draft.display.search.countries)} onChange={e => patchDisplay({ search: { ...draft.display.search, countries: parseCsv(e.target.value).map(s => s.toUpperCase()) } })} />
              </div>
            </div>

            <h6 className="fw-semibold small mt-3 mb-2">Theme</h6>
            <div className="row g-2">
              {THEME_COLOR_TOKENS.map(({ token, label }) => (
                <div className="col-6 col-md-3" key={token}>
                  <ColorField label={label} value={typeof draft.display.theme[token] === 'string' ? String(draft.display.theme[token]) : undefined} onChange={v => patchTheme(token, v)} defaultLabel="Style" />
                </div>
              ))}
              <div className="col-6 col-md-3">
                <label className="form-label small mb-1">Lock mask opacity</label>
                <input type="number" min={0} max={1} step={0.05} className="form-control" value={typeof draft.display.theme.maskOpacity === 'number' ? draft.display.theme.maskOpacity : ''} placeholder="0.55" onChange={e => patchTheme('maskOpacity', e.target.value === '' ? undefined : Number(e.target.value))} />
              </div>
              <div className="col-12 d-flex flex-wrap gap-4 small">
                {THEME_BOOL_TOKENS.map(({ token, label }) => (
                  <div className="form-check" key={token}>
                    <input type="checkbox" className="form-check-input" id={`lm-tb-${token}`} checked={draft.display.theme[token] === true} onChange={e => patchTheme(token, e.target.checked ? true : (draft.display.theme[token] === undefined ? false : undefined))} />
                    <label className="form-check-label" htmlFor={`lm-tb-${token}`}>{label}</label>
                  </div>
                ))}
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => patchDisplay({ theme: {} })}><RotateCcw size={12} /> Clear theme</button>
              </div>
              <div className="col-12">
                <label className="form-label small mb-1">Paste a theme JSON (the website&apos;s retailermaps theme files work as-is)</label>
                <div className="input-group">
                  <textarea className="form-control font-monospace" rows={2} value={themeJson} onChange={e => setThemeJson(e.target.value)} placeholder='{"tokens":{"water":"#c7d8ec", ...}}' />
                  <button type="button" className="btn btn-outline-secondary" onClick={applyThemeJson} disabled={!themeJson.trim()}>Apply</button>
                </div>
              </div>
            </div>
          </div>

          {/* Templates */}
          <div className="border rounded-3 p-3 mb-3">
            <h5 className="fw-semibold mb-1">Templates (Handlebars)</h5>
            <p className="text-muted small mb-2">The same placeholders and syntax as the website&apos;s map chunks, rendered once per location. Copy is translatable through <code>{'{{t "key"}}'}</code> and the Strings section below.</p>
            <div className="row g-3">
              {(['card', 'infowindow', 'noresults'] as const).map(k => (
                <div className="col-lg-4" key={k}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="form-label small fw-semibold mb-0">{k === 'card' ? 'Card (list)' : k === 'infowindow' ? 'Popup (pin)' : 'No results'}</label>
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => patchDraft({ templates: { ...draft.templates, [k]: DEFAULT_TEMPLATES[k] } })}><RotateCcw size={12} /> Reset to default</button>
                  </div>
                  <textarea className="form-control font-monospace" rows={12} spellCheck={false} value={draft.templates[k]} onChange={e => patchDraft({ templates: { ...draft.templates, [k]: e.target.value } })} />
                </div>
              ))}
            </div>
            {preview?.error && <div className="alert alert-danger py-2 small mt-2 mb-0"><AlertTriangle size={14} /> {preview.error}</div>}
            <div className="row g-3 mt-1">
              <div className="col-lg-5">
                <div className="small fw-semibold text-muted mb-1">Preview — card</div>
                <div className="lm" style={{ maxWidth: 420 }}>
                  {/* Rendered through the same sanitising pipeline as the results page. */}
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: preview?.card ?? '' }} />
                </div>
              </div>
              <div className="col-lg-4">
                <div className="small fw-semibold text-muted mb-1">Preview — popup</div>
                <div className="lm border rounded p-2 bg-white" style={{ maxWidth: 320 }}>
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: preview?.info ?? '' }} />
                </div>
                <div className="small fw-semibold text-muted mb-1 mt-3">Preview — no results</div>
                <div className="lm border rounded p-2 bg-white" style={{ maxWidth: 320 }}>
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: preview?.none ?? '' }} />
                </div>
              </div>
              <div className="col-lg-3">
                <details>
                  <summary className="small fw-semibold">Placeholders</summary>
                  <table className="table table-sm small mt-2"><tbody>{PLACEHOLDER_LEGEND.map(p => <tr key={p.field}><td className="font-monospace text-nowrap">{p.field}</td><td>{p.description}</td></tr>)}</tbody></table>
                </details>
                <details className="mt-2">
                  <summary className="small fw-semibold">Helpers</summary>
                  <table className="table table-sm small mt-2"><tbody>{HELPER_LEGEND.map(h => <tr key={h.helper}><td className="font-monospace">{h.helper}</td><td>{h.description}</td></tr>)}</tbody></table>
                </details>
              </div>
            </div>
          </div>

          {/* Strings */}
          <div className="border rounded-3 p-3 mb-3">
            <h5 className="fw-semibold mb-1">Strings</h5>
            <p className="text-muted small mb-2">Copy the templates pull in with <code>{'{{t "key"}}'}</code>. Blank = the built-in translated text. Other languages are entered below and saved together with the English.</p>
            <div className="row g-2">
              {MAP_STRING_KEYS.map(k => (
                <div className="col-md-4 col-lg-3" key={k}>
                  <label className="form-label small mb-1 font-monospace">{k}</label>
                  <input className="form-control" value={draft.strings[k] ?? ''} placeholder="built-in" onChange={e => { const strings = { ...draft.strings }; if (e.target.value) strings[k] = e.target.value; else delete strings[k]; patchDraft({ strings }); }} />
                </div>
              ))}
            </div>
            <TranslationFields
              entityType="location_maps"
              entityId={draft.id > 0 ? draft.id : null}
              fields={translatableFields}
              values={translations}
              onChange={setTranslations}
              title="Map copy in other languages"
            />
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <button type="button" className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" onClick={() => void save()} disabled={saving || !draft.name.trim()}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {draft.id > 0 ? 'Save map' : 'Create map'}
            </button>
            {draft.id > 0 && (
              <button type="button" className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1 ms-auto" onClick={() => void remove(draft)} disabled={saving}>
                <Trash2 size={14} /> Delete map
              </button>
            )}
          </div>

          {iconPickerOpen && (
            <IconPicker
              retailerId={0}
              onSelect={icon => { patchHeading({ icon }); setIconPickerOpen(false); }}
              onClose={() => setIconPickerOpen(false)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AdminLocationMaps;
